import * as fal from '@fal-ai/serverless-client'

// Configure fal client
console.log('🔧 Configuring fal client...')
console.log('🔑 FAL_KEY exists:', !!import.meta.env.VITE_FAL_KEY)
console.log('🔑 FAL_KEY length:', import.meta.env.VITE_FAL_KEY?.length || 0)

fal.config({
  credentials: import.meta.env.VITE_FAL_KEY
})

console.log('✅ Fal client configured successfully')

/**
 * Generate video using fal.ai API
 * @param {string} prompt - The text prompt for video generation
 * @param {string} videoType - Type of video generation ('text_to_video' or 'image_to_video')
 * @param {Object} options - Additional options
 * @param {File} options.imageFile - Image file for image-to-video generation
 * @param {string} options.imagePrompt - Additional prompt for image-to-video
 * @returns {Promise<Object>} Generated video data
 */
export const generateVideo = async (prompt, videoType = 'text_to_video', options = {}) => {
  console.log('🚀 Starting generateVideo function...')
  console.log('📝 Prompt:', prompt)
  console.log('🎬 Video Type:', videoType)
  console.log('⚙️ Options:', options)
  
  // Validate API key first
  const apiKey = import.meta.env.VITE_FAL_KEY
  if (!apiKey) {
    throw new Error('FAL API Key tidak tersedia. Silakan set VITE_FAL_KEY di environment variable.')
  }
  
  try {
    let modelEndpoint
    let requestData

    // Get aspect ratio from options, default to 16:9
    const aspectRatio = options.aspectRatio || '16:9'
    console.log('📐 Aspect Ratio:', aspectRatio)
    
    if (videoType === 'text_to_video') {
      modelEndpoint = 'fal-ai/wan/v2.2-a14b/text-to-video/turbo'
      requestData = {
        prompt: prompt,
        num_frames: 81, // Default frames (about 5 seconds at 16fps)
        frames_per_second: 16, // Default FPS for turbo model
        resolution: "720p", // Default resolution
        aspect_ratio: aspectRatio === '1:1' ? '16:9' : aspectRatio, // 1:1 not supported, fallback to 16:9
        seed: Math.floor(Math.random() * 1000000),
        enable_safety_checker: true,
        enable_prompt_expansion: false
      }
    } else if (videoType === 'image_to_video') {
      modelEndpoint = 'fal-ai/wan/v2.2-a14b/image-to-video/turbo'
      
      if (!options.imageFile) {
        throw new Error('Image file is required for image-to-video generation')
      }

      // Convert image file to base64 or upload to fal
      const imageUrl = await uploadImageToFal(options.imageFile)
      
      requestData = {
        image_url: imageUrl,
        prompt: options.imagePrompt || prompt,
        num_frames: 81, // Default frames (about 5 seconds at 16fps)
        frames_per_second: 16, // Default FPS for turbo model
        resolution: "720p", // Default resolution
        aspect_ratio: aspectRatio === '1:1' ? '16:9' : aspectRatio, // 1:1 not supported, fallback to 16:9
        seed: Math.floor(Math.random() * 1000000),
        enable_safety_checker: true,
        enable_prompt_expansion: false
      }
    } else {
      throw new Error(`Unsupported video type: ${videoType}`)
    }

    console.log('🔄 Generating video with fal.ai:', { modelEndpoint, requestData })
    console.log('📡 About to call fal.queue.submit...')
    console.log('🔑 Using API key (first 10 chars):', apiKey.substring(0, 10) + '...')
    console.log('📋 Request payload:', JSON.stringify({ input: requestData }, null, 2))

    // Submit to queue first to get request ID
    let request_id
    try {
      const response = await fal.queue.submit(modelEndpoint, {
        input: requestData
      })
      request_id = response.request_id
      console.log('📋 Video request submitted with ID:', request_id)
    } catch (submitError) {
      console.error('❌ Error submitting request:', submitError)
      if (submitError.message && submitError.message.includes('422')) {
        throw new Error('API request tidak valid. Periksa parameter video atau coba lagi nanti. Detail: ' + submitError.message)
      }
      throw submitError
    }
    
    // Now poll for the result with proper timeout handling
    let attempts = 0
    const maxAttempts = 150 // 5 minutes with 2-second intervals
    
    while (attempts < maxAttempts) {
      try {
        const status = await fal.queue.status(modelEndpoint, {
          requestId: request_id,
          logs: true
        })
        
        console.log(`📊 Attempt ${attempts + 1}: Status check result:`, status)
        
        if (status.status === 'COMPLETED') {
          console.log('✅ Video berhasil dibuat!')
          const result = await fal.queue.result(modelEndpoint, {
            requestId: request_id
          })
          console.log('📡 Final result retrieved:', result)
          break
        } else if (status.status === 'FAILED') {
          throw new Error(`Video generation failed: ${status.error || 'Unknown error'}`)
        } else if (status.status === 'IN_QUEUE') {
          console.log(`🔄 Video sedang dalam antrian... (attempt ${attempts + 1}/${maxAttempts})`)
        } else if (status.status === 'IN_PROGRESS') {
          console.log(`⚡ Video sedang diproses... (attempt ${attempts + 1}/${maxAttempts})`)
        }
        
        // Wait 2 seconds before next check
        await new Promise(resolve => setTimeout(resolve, 2000))
        attempts++
        
      } catch (error) {
        console.error('❌ Error during status check:', error)
        
        // Handle 422 errors specifically
        if (error.message && error.message.includes('422')) {
          console.error('🚫 422 Error - Request tidak valid atau API key bermasalah')
          throw new Error('Request video tidak valid atau terjadi masalah autentikasi. Silakan coba lagi atau periksa konfigurasi API.')
        }
        
        attempts++
        if (attempts >= maxAttempts) {
          // Store request ID for manual retrieval
          console.log('⚠️ Timeout reached, but request ID saved for manual retrieval:', request_id)
          throw new Error(`Client timed out waiting for the request to complete after 300000ms. Request ID: ${request_id}. Video mungkin sudah selesai di dashboard fal.ai.`)
        }
        await new Promise(resolve => setTimeout(resolve, 2000))
      }
    }
    
    if (attempts >= maxAttempts) {
      console.log('⚠️ Max attempts reached, but request ID saved for manual retrieval:', request_id)
      throw new Error(`Client timed out waiting for the request to complete after 300000ms. Request ID: ${request_id}. Video mungkin sudah selesai di dashboard fal.ai.`)
    }

    // Get the final result if we broke out of the loop successfully
    const finalResult = await fal.queue.result(modelEndpoint, {
      requestId: request_id
    })
    
    console.log('Video generation result:', finalResult)
    console.log('Result data:', finalResult.data)
    console.log('Result video:', finalResult.video)
    console.log('Result type:', typeof finalResult)
    
    const result = finalResult // For compatibility with existing code
    
    // Handle different response structures from fal.ai API
    let videoData
    
    // Check if video data is directly in result.video (new API response format)
    if (result.video && result.video.url) {
      videoData = result.video
    }
    // Check if video data is in result.data.video (old format)
    else if (result.data && result.data.video && result.data.video.url) {
      videoData = result.data.video
    }
    // Check if video URL is directly in result.data.url
    else if (result.data && result.data.url) {
      videoData = { url: result.data.url }
    }
    // Check if result.data is a string URL
    else if (result.data && typeof result.data === 'string') {
      videoData = { url: result.data }
    }
    // Check if video URL is directly in result.url
    else if (result.url) {
      videoData = { url: result.url }
    }
    else {
      console.error('No video data found in response:', result)
      throw new Error('Invalid response from fal.ai API: No video data found')
    }
    
    if (!videoData || !videoData.url) {
      console.error('No video URL found in response:', videoData)
      throw new Error('Invalid response from fal.ai API: No video URL')
    }

    return {
      url: videoData.url,
      videoUrl: videoData.url,
      duration: Math.round((requestData.num_frames / requestData.frames_per_second) * 100) / 100, // Calculate duration from frames and fps
      fps: requestData.frames_per_second,
      aspectRatio: requestData.aspect_ratio,
      width: getWidthFromAspectRatio(requestData.aspect_ratio),
      height: getHeightFromAspectRatio(requestData.aspect_ratio),
      seed: result.seed || requestData.seed,
      prompt: result.prompt || prompt,
      videoType: videoType,
      requestId: request_id, // Add request ID for manual retrieval
      generatedAt: new Date().toISOString(),
      metadata: {
        modelEndpoint,
        requestData,
        response: result,
        requestId: request_id
      }
    }
  } catch (error) {
    console.error('❌ Error generating video:', error)
    console.error('❌ Error name:', error.name)
    console.error('❌ Error message:', error.message)
    console.error('❌ Error stack:', error.stack)
    console.error('❌ Full error object:', JSON.stringify(error, null, 2))
    
    // Check if this is a network/connection error
    if (error.name === 'TypeError' && error.message?.includes('fetch')) {
      throw new Error('Gagal terhubung ke server fal.ai. Periksa koneksi internet Anda dan coba lagi.')
    }
    
    // Check if this is a CORS error
    if (error.message?.includes('CORS') || error.message?.includes('cross-origin')) {
      throw new Error('Masalah CORS dengan fal.ai API. Silakan hubungi administrator.')
    }
    
    // Check if this is an authentication error
    if (error.message?.includes('401') || error.message?.includes('Unauthorized') || error.message?.includes('Authentication')) {
      throw new Error('API Key fal.ai tidak valid atau sudah expired. Silakan periksa konfigurasi.')
    }
    
    // Handle specific error cases
    if (error.message?.includes('rate limit')) {
      throw new Error('Terlalu banyak permintaan. Silakan coba lagi dalam beberapa menit.')
    }
    
    if (error.message?.includes('insufficient credits')) {
      throw new Error('Kredit tidak mencukupi untuk menggunakan layanan ini.')
    }
    
    if (error.message?.includes('content policy')) {
      throw new Error('Konten tidak sesuai dengan kebijakan. Silakan gunakan prompt yang berbeda.')
    }
    
    if (error.message?.includes('timeout') || error.name === 'TimeoutError') {
      throw new Error('Proses pembuatan video memakan waktu terlalu lama. Video mungkin sudah selesai di dashboard fal.ai, silakan coba lagi atau periksa dashboard fal.ai Anda.')
    }
    
    if (error.message?.includes('queue') || error.message?.includes('IN_QUEUE')) {
      throw new Error('Video masih dalam antrian pemrosesan. Silakan tunggu beberapa menit dan coba lagi.')
    }
    
    if (error.message?.includes('access')) {
      throw new Error('Masalah autentikasi dengan fal.ai. Silakan periksa konfigurasi API key.')
    }
    
    // If no specific error pattern matches, provide the original error message
    throw new Error(error.message || 'Gagal membuat video. Silakan coba lagi.')
  }
}

/**
 * Upload image to fal.ai storage
 * @param {File} imageFile - Image file to upload
 * @returns {Promise<string>} Image URL
 */
const uploadImageToFal = async (imageFile) => {
  try {
    // Convert file to base64
    const base64 = await fileToBase64(imageFile)
    
    // Upload to fal storage
    const uploadResult = await fal.storage.upload(base64)
    
    return uploadResult.url
  } catch (error) {
    console.error('Error uploading image to fal:', error)
    throw new Error('Gagal mengupload gambar. Silakan coba lagi.')
  }
}

/**
 * Convert file to base64
 * @param {File} file - File to convert
 * @returns {Promise<string>} Base64 string
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })
}

/**
 * Get available video types
 * @returns {Array} Array of video type options
 */
export const getAvailableVideoTypes = () => {
  return [
    {
      value: 'text_to_video',
      label: 'Text to Video',
      description: 'Buat video dari deskripsi teks'
    },
    {
      value: 'image_to_video',
      label: 'Image to Video',
      description: 'Buat video dari gambar dan prompt'
    }
  ]
}

/**
 * Download video from URL
 * @param {string} videoUrl - URL of the video to download
 * @param {string} filename - Filename for the downloaded video
 */
export const downloadVideo = async (videoUrl, filename = 'video') => {
  try {
    const response = await fetch(videoUrl)
    if (!response.ok) {
      throw new Error('Failed to fetch video')
    }
    
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.mp4`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Clean up the object URL
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading video:', error)
    throw new Error('Gagal mengunduh video. Silakan coba lagi.')
  }
}

/**
 * Validate video generation parameters
 * @param {string} prompt - The prompt to validate
 * @param {string} videoType - The video type to validate
 * @param {Object} options - Additional options to validate
 * @returns {Object} Validation result
 */
export const validateVideoParams = (prompt, videoType, options = {}) => {
  const errors = []
  
  // Validate prompt
  if (!prompt || prompt.trim().length === 0) {
    errors.push('Prompt tidak boleh kosong')
  }
  
  if (prompt && prompt.length > 1000) {
    errors.push('Prompt terlalu panjang (maksimal 1000 karakter)')
  }
  
  // Validate video type
  const validVideoTypes = ['text_to_video', 'image_to_video']
  if (!validVideoTypes.includes(videoType)) {
    errors.push('Tipe video tidak valid')
  }
  
  // Validate image for image-to-video
  if (videoType === 'image_to_video') {
    if (!options.imageFile) {
      errors.push('Gambar diperlukan untuk Image to Video')
    } else {
      // Validate image file
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
      if (!validImageTypes.includes(options.imageFile.type)) {
        errors.push('Format gambar tidak didukung. Gunakan JPEG, PNG, atau WebP')
      }
      
      // Check file size (max 10MB)
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (options.imageFile.size > maxSize) {
        errors.push('Ukuran gambar terlalu besar (maksimal 10MB)')
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Get video file extension from URL
 * @param {string} videoUrl - Video URL
 * @returns {string} File extension
 */
export const getVideoFileExtension = (videoUrl) => {
  try {
    const url = new URL(videoUrl)
    const pathname = url.pathname
    const extension = pathname.split('.').pop().toLowerCase()
    
    // Default to mp4 if no extension found
    return ['mp4', 'webm', 'mov', 'avi'].includes(extension) ? extension : 'mp4'
  } catch (error) {
    return 'mp4'
  }
}

/**
 * Format video duration
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration
 */
export const formatVideoDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0:00'
  
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

/**
 * Get video quality options
 * @returns {Array} Array of quality options
 */
export const getVideoQualityOptions = () => {
  return [
    {
      value: '512x512',
      label: 'Standard (512x512)',
      description: 'Kualitas standar, render cepat'
    },
    {
      value: '768x768',
      label: 'High (768x768)',
      description: 'Kualitas tinggi, render sedang'
    },
    {
      value: '1024x1024',
      label: 'Ultra (1024x1024)',
      description: 'Kualitas ultra, render lambat'
    }
  ]
}

/**
 * Get video duration options
 * @returns {Array} Array of duration options
 */
export const getVideoDurationOptions = () => {
  return [
    {
      value: 3,
      label: '3 detik',
      description: 'Durasi pendek'
    },
    {
      value: 5,
      label: '5 detik',
      description: 'Durasi standar'
    },
    {
      value: 10,
      label: '10 detik',
      description: 'Durasi panjang'
    }
  ]
}

/**
 * Get aspect ratio options
 * @returns {Array} Array of aspect ratio options
 */
export const getAspectRatioOptions = () => {
  return [
    {
      value: '16:9',
      label: '16:9',
      description: 'Landscape (Widescreen)'
    },
    {
      value: '9:16',
      label: '9:16',
      description: 'Portrait (Mobile)'
    }
    // Note: 1:1 aspect ratio is not supported by fal.ai wan v2.2-a14b turbo models
    // It will automatically fallback to 16:9 if selected
  ]
}

/**
 * Get width from aspect ratio
 * @param {string} aspectRatio - Aspect ratio string (e.g., '16:9')
 * @returns {number} Width in pixels
 */
export const getWidthFromAspectRatio = (aspectRatio) => {
  const ratioMap = {
    '16:9': 1024,
    '9:16': 576,
    '1:1': 768
  }
  return ratioMap[aspectRatio] || 1024
}

/**
 * Get height from aspect ratio
 * @param {string} aspectRatio - Aspect ratio string (e.g., '16:9')
 * @returns {number} Height in pixels
 */
export const getHeightFromAspectRatio = (aspectRatio) => {
  const ratioMap = {
    '16:9': 576,
    '9:16': 1024,
    '1:1': 768
  }
  return ratioMap[aspectRatio] || 576
}

/**
 * Check status of existing video request
 * @param {string} requestId - The request ID from fal.ai
 * @returns {Promise<Object>} Video status and data if completed
 */
export const checkVideoStatus = async (requestId) => {
  try {
    console.log('🔍 Checking video status for request ID:', requestId)
    
    // Try to get status from both text-to-video and image-to-video endpoints
    let result
    try {
      result = await fal.queue.status('fal-ai/wan/v2.2-a14b/text-to-video/turbo', {
        requestId: requestId
      })
    } catch (error) {
      // If text-to-video fails, try image-to-video endpoint
      console.log('🔄 Trying image-to-video endpoint for status check...')
      result = await fal.queue.status('fal-ai/wan/v2.2-a14b/image-to-video/turbo', {
        requestId: requestId
      })
    }
    
    console.log('📊 Video status result:', result)
    
    if (result.status === 'COMPLETED' && result.data) {
      // Process the completed video data
      let videoData
      
      if (result.data.video && result.data.video.url) {
        videoData = result.data.video
      } else if (result.data.url) {
        videoData = { url: result.data.url }
      } else {
        throw new Error('No video data found in completed result')
      }
      
      return {
        status: 'COMPLETED',
        video: {
          url: videoData.url,
          videoUrl: videoData.url,
          duration: Math.round((81 / 16) * 100) / 100, // Default calculation: 81 frames / 16 fps
          fps: 16, // Default FPS for turbo model
          seed: result.data.seed,
          generatedAt: new Date().toISOString(),
          requestId: requestId
        }
      }
    }
    
    return {
      status: result.status,
      message: result.status === 'IN_QUEUE' ? 'Video masih dalam antrian' :
               result.status === 'IN_PROGRESS' ? 'Video sedang diproses' :
               result.status === 'FAILED' ? 'Pembuatan video gagal' :
               'Status tidak dikenal'
    }
    
  } catch (error) {
    console.error('❌ Error checking video status:', error)
    throw new Error(`Gagal mengecek status video: ${error.message}`)
  }
}

/**
 * Retrieve completed video by request ID
 * @param {string} requestId - The request ID from fal.ai
 * @returns {Promise<Object>} Video data if completed
 */
export const getCompletedVideo = async (requestId) => {
  try {
    const statusResult = await checkVideoStatus(requestId)
    
    if (statusResult.status === 'COMPLETED') {
      console.log('✅ Video sudah selesai, mengambil data...')
      return statusResult.video
    } else {
      throw new Error(`Video belum selesai. Status: ${statusResult.status}. ${statusResult.message}`)
    }
    
  } catch (error) {
    console.error('❌ Error getting completed video:', error)
    throw new Error(`Gagal mengambil video yang sudah selesai: ${error.message}`)
  }
}